import * as React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Circle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DayCloseLabels, DayCloseWizardStep } from "./types";
import { STEP_ORDER } from "./utils";

export interface DayCloseStepIndicatorProps {
  currentStep: DayCloseWizardStep;
  labels: DayCloseLabels;
  blockedSteps?: readonly DayCloseWizardStep[];
  onStepClick?: (s: DayCloseWizardStep) => void;
  className?: string;
}

export function DayCloseStepIndicator({
  currentStep, labels, blockedSteps = [], onStepClick, className,
}: DayCloseStepIndicatorProps) {
  const currentIdx = STEP_ORDER.indexOf(currentStep);
  return (
    <ol
      className={cn("flex items-center gap-2 px-6 py-3 bg-biz-surface border-b border-biz-border", className)}
      aria-label={labels.page.title}
    >
      {STEP_ORDER.map((s, i) => {
        const stepLabel = labels.steps[s].key;
        const completed = i < currentIdx;
        const active = s === currentStep;
        const isBlocked = blockedSteps.includes(s);
        return (
          <React.Fragment key={s}>
            <li>
              <button
                type="button"
                onClick={() => onStepClick?.(s)}
                disabled={!onStepClick || isBlocked}
                aria-current={active ? "step" : undefined}
                className={cn(
                  "flex items-center gap-2 h-10 px-3 rounded-biz-btn font-semibold text-sm transition-colors biz-focus-ring",
                  active && "bg-biz-primary text-biz-primary-foreground biz-shadow-sm",
                  completed && !active && "bg-biz-success-subtle text-biz-success border border-biz-success-border",
                  !active && !completed && "bg-biz-surface-elevated text-biz-foreground-muted border border-biz-border",
                  onStepClick && !isBlocked && "hover:opacity-90 cursor-pointer",
                )}
              >
                <span
                  className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full text-xs",
                    active && "bg-biz-primary-foreground text-biz-primary",
                    completed && !active && "text-biz-success",
                    !active && !completed && "border border-biz-border-strong text-biz-foreground-subtle",
                  )}
                  aria-hidden
                >
                  {completed ? (
                    <CheckCircle weight="fill" className="w-5 h-5" />
                  ) : active ? (
                    <span className="font-bold">{i + 1}</span>
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </span>
                <span className="hidden md:inline">{stepLabel}</span>
              </button>
            </li>
            {i < STEP_ORDER.length - 1 && (
              <li aria-hidden className="flex-1 max-w-[40px]">
                <motion.div
                  initial={false}
                  animate={{ scaleX: completed ? 1 : 0.4, opacity: completed ? 1 : 0.4 }}
                  className={cn(
                    "h-px origin-left",
                    completed ? "bg-biz-success" : "bg-biz-border",
                  )}
                />
              </li>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}
